import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import type {BoxSelectionProps, DropdownOptionProps, DropdownProps} from '@wix/wix-ui-tpa/cssVars';
import {BoxSelection, BoxSelectionBoxSize, Spinner, SpinnerTypes} from '@wix/wix-ui-tpa/cssVars';
import {ButtonOptionWidth, Experiments, TextualOptionType} from '../../../../../constants';
import {useGlobals, withGlobals} from '../../../../../globalPropsContext';
import {IProduct} from '../../../../../types/galleryTypes';
import {LazyDropDown} from '../../../../lazyComponents/lazyWUTComponents';
import {classes as dropdownOptionStyles, style as styleDropdown} from '../DropdownOption.st.css';
import {DataHook, ProductOptionsChangeData} from '../ProductOptions';
import {classes as boxSelectionStyles} from './ProductTextualOption.st.css';
import s from './ProductTextualOptions.scss';
import {useOptionSelectionLogic} from '../../hooks/options/useOptionSelectionLogic';
import {buildUrlWithHash} from '../../../../url/productPageUrl';

type IProductOption = IProduct['options'][0];

const renderInnerSelection = (
  selection: DropdownOptionProps,
  index: number,
  styles: Record<string, string>,
  isUnreachable: boolean,
  isSelected: boolean,
  isGridMode: boolean
) => {
  return (
    <div
      data-hook={`box-selection-text-option-${index}`}
      className={classNames(styles.optionItemText, {
        [styles.disabled]: isUnreachable,
        [styles.selected]: isSelected,
        [styles.gridMode]: isGridMode,
      })}>
      {selection.value}
    </div>
  );
};

const getShownSelectionsAndSuffix = ({
  visibleSelections,
  showButtons,
  shouldLimitButtonChoices,
  productOptionButtonLimit,
  productUrl,
  isLiveSiteMode,
  isPreviewMode,
  styles,
  t,
}: {
  visibleSelections: IProductOption['selections'];
  showButtons: boolean;
  shouldLimitButtonChoices: boolean;
  productOptionButtonLimit: number;
  productUrl: string;
  isLiveSiteMode: boolean;
  isPreviewMode: boolean;
  styles: Record<string, string>;
  t: IProvidedTranslationProps['t'];
}) => {
  const shouldSlice = showButtons && shouldLimitButtonChoices;
  const shownSelections = shouldSlice ? visibleSelections.slice(0, productOptionButtonLimit) : visibleSelections;
  const remainingOptionsCounter = visibleSelections.length - productOptionButtonLimit;
  const suffix =
    !shouldLimitButtonChoices || remainingOptionsCounter <= 0 ? null : (
      <a
        dir="ltr"
        href={
          isLiveSiteMode || isPreviewMode ? buildUrlWithHash(productUrl, `${VIEW_MORE_OPTIONS_URL_FRAGMENT}-1`) : null
        }
        data-hook={DataHook.ButtonOptionMore}
        className={styles.remainingOptionsCounter}
        aria-label={t('buttonOptionNumMoreOptionsAriaLabel', {numRemainingChoices: remainingOptionsCounter})}>
        {t('buttonOptionNumChoicesLeftLabel', {numChoiceLeft: remainingOptionsCounter})}
      </a>
    );
  return {
    suffix,
    shownSelections,
  };
};

export interface TextualOptionProps {
  option: IProductOption;
  availability: Record<string, {isVisible: boolean; isSelectable: boolean}>;
  selectionIds: number[];
  readOnly?: boolean;
  isLoading: boolean;
  productUrl: string;
  onSelectionIdsChange: (optionSelections: number[], changeData: ProductOptionsChangeData) => Promise<void>;
  isGroupedByOption?: boolean;
}

/* eslint-disable sonarjs/cognitive-complexity */
const TextualOption: React.FC<TextualOptionProps & IProvidedTranslationProps> = ({
  option,
  availability,
  selectionIds,
  readOnly,
  isLoading,
  productUrl,
  onSelectionIdsChange,
  isGroupedByOption,
  t,
}) => {
  const {
    useExperiments,
    shouldShowMobile,
    styles,
    stylesParams,
    isLiveSiteMode,
    isPreviewMode,
    host,
    isSSR,
    isSliderGallery,
  } = useGlobals();
  const isButtonOptionsEnabled = useExperiments.enabled(Experiments.GalleryProductOptionsTextualOptionType);
  const textualOptionType = styles.get(stylesParams.gallery_textualOptionType);
  const showButtons = isButtonOptionsEnabled && textualOptionType === TextualOptionType.BUTTONS;
  const buttonOptionWidth = styles.get(stylesParams.gallery_buttonOptionWidth);
  const isGridMode = buttonOptionWidth === ButtonOptionWidth.STRETCH;

  const {
    visibleSelections,
    selectedSelectionId,
    calculateNextSelectionIds,
    isSelectionDisabled,
    isSelectionUnreachable,
    isSelectionMarkedUnavailable,
    getSelectionAriaLabel,
    isSelectionChecked,
  } = useOptionSelectionLogic({
    option,
    selectionIds,
    selectionsAvailability: availability,
  });

  const handleOptionChange = (id: string) => {
    const pickedSelectionId = parseInt(id, 10);
    const nextSelectionIds = calculateNextSelectionIds(pickedSelectionId);
    void onSelectionIdsChange(nextSelectionIds, {
      optionType: ProductOptionType.DROP_DOWN,
    });
  };

  const sealButtonChangeHandler: BoxSelectionProps['onChange'] = ({id}) => {
    const parts = id.split('-');
    const selectionId = parts[parts.length - 1];
    handleOptionChange(selectionId);
  };

  const sealDropdownChangeHandler: DropdownProps['onChange'] = ({id}) => {
    handleOptionChange(id);
  };

  const shouldAllowCustomProductOptionsAndQuantityWidth = useExperiments.enabled(
    Experiments.GalleryProductOptionsAndQuantityWidth
  );

  const renderLoader = () => {
    return (
      <div data-hook={DataHook.ProductOptionLoader}>
        <Spinner type={SpinnerTypes.slim} className={s.loader} />
      </div>
    );
  };

  const {title, id} = option;
  const productOptionButtonLimit = styles.get(stylesParams.gallery_productOptionButtonChoicesLimit);
  const shouldLimitButtonChoices = styles.get(stylesParams.gallery_shouldLimitButtonChoices);
  const {suffix, shownSelections} = getShownSelectionsAndSuffix({
    visibleSelections,
    showButtons,
    shouldLimitButtonChoices,
    productOptionButtonLimit,
    productUrl,
    isLiveSiteMode,
    isPreviewMode,
    styles: s,
    t,
  });

  const options: DropdownOptionProps[] = shownSelections.map((selection) => ({
    id: selection.id.toString(),
    isSelectable: !isSelectionDisabled(selection.id),
    value: selection.description,
    className: isSelectionMarkedUnavailable(selection.id) ? s.unreachableDropdownOption : undefined,
  }));

  const renderButtonOptions = () =>
    options.map((selection, index) => {
      const selectionId = parseInt(selection.id, 10);
      const isDisabled = isSelectionDisabled(selectionId);
      const isUnreachable = isSelectionUnreachable(selectionId);
      const isSelected = !isDisabled && isSelectionChecked(selectionId);

      return (
        <BoxSelection.Option
          key={selection.id}
          id={`${option.id}-${selection.id}`}
          unstableHasInnerWrapper={false}
          checked={isGroupedByOption ? isSelectionChecked(selectionId) : isSelected}
          aria-label={getSelectionAriaLabel(selectionId, selection.value)}
          disabled={isGroupedByOption ? false : isDisabled}
          data-hook={`box-selection-option-${index}`}
          className={classNames(boxSelectionStyles.buttonOptionItem, s.buttonOptionItemContainer, {
            [s.gridMode]: isGridMode,
          })}>
          {renderInnerSelection(selection, index, s, isUnreachable, isSelected, isGridMode)}
        </BoxSelection.Option>
      );
    });

  const renderBoxSelectionOptions = () => (
    <BoxSelection
      name={`button-option-${id}`}
      aria-label={title}
      size={BoxSelectionBoxSize.xSmall}
      onChange={sealButtonChangeHandler}
      data-hook={DataHook.ButtonOption}
      className={classNames(s.buttonOption, boxSelectionStyles.buttonOptionItem, {
        [s.gridMode]: isGridMode,
        [boxSelectionStyles.gridMode]: isGridMode,
      })}
      suffix={suffix}
      suffixClassName={s.buttonOptionSuffix}
      grid={isGridMode}>
      {isLoading ? renderLoader() : renderButtonOptions()}
    </BoxSelection>
  );

  /* istanbul ignore next */
  const appendTo = useMemo(() => {
    if (isSSR || !isSliderGallery) {
      return undefined;
    }
    return document.getElementById('SITE_CONTAINER');
  }, [isSSR, isSliderGallery]);

  const renderDropdownOptions = () => (
    <LazyDropDown
      appendTo={appendTo}
      className={
        /* istanbul ignore next */
        classNames(
          host.id,
          styleDropdown(dropdownOptionStyles.dropdownOption, {
            separateStyles: true,
          }),
          {
            [s.dropdownWidth]: shouldAllowCustomProductOptionsAndQuantityWidth,
            [s.isMobile]: shouldAllowCustomProductOptionsAndQuantityWidth && shouldShowMobile,
          }
        )
      }
      data-hook={DataHook.DropdownOption}
      aria-label={title}
      placeholder={title}
      options={options}
      selectedId={selectedSelectionId || 'no selection'}
      onChange={sealDropdownChangeHandler}
      isLoading={isLoading}
      readOnly={readOnly}
      mobileNativeSelect={shouldShowMobile}
    />
  );

  return (
    <div
      data-hook={DataHook.ProductOption}
      className={classNames({
        [s.dropdownAlignment]: shouldAllowCustomProductOptionsAndQuantityWidth,
        [s.productOptionGridMode]: isGridMode,
      })}>
      {showButtons ? renderBoxSelectionOptions() : renderDropdownOptions()}
    </div>
  );
};
/* eslint-enable sonarjs/cognitive-complexity */

export const ProductTextualOption = withGlobals(withTranslations()(TextualOption));
